import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js'

/** Bare national numbers (no + / 00) still try HU first — home market convenience. */
const LOCAL_FALLBACK_COUNTRY: CountryCode = 'HU'

function toInternationalInput(phone: string): string {
  if (phone.startsWith('00')) {
    return `+${phone.slice(2)}`
  }
  return phone
}

function digitsOnly(phone: string): string {
  return phone.replace(/\D/g, '')
}

/**
 * Parse any country. Accept = libphonenumber `isPossible()` (not `isValid()`, not HU-only).
 * Order: +/00 international → HU local → +digits guess.
 */
function tryParsePossiblePhone(phone: string) {
  const trimmed = phone.trim()
  if (!trimmed) {
    return undefined
  }

  const input = toInternationalInput(trimmed)
  const candidates = []

  if (input.startsWith('+')) {
    candidates.push(parsePhoneNumberFromString(input))
  } else {
    candidates.push(parsePhoneNumberFromString(input, LOCAL_FALLBACK_COUNTRY))
    const digits = digitsOnly(input)
    if (digits.length >= 8) {
      candidates.push(parsePhoneNumberFromString(`+${digits}`))
    }
  }

  for (const parsed of candidates) {
    if (parsed?.isPossible() === true) {
      return parsed
    }
  }

  return undefined
}

/** True when non-empty input is a possible phone number (any country). */
export function isPossiblePhone(phone: string): boolean {
  return tryParsePossiblePhone(phone) !== undefined
}

/** Empty / whitespace OK. Non-empty must be `isPossible`. */
export function isAbsentOrPossiblePhone(phone: string | null | undefined): boolean {
  const trimmed = phone?.trim() ?? ''
  return trimmed.length === 0 || isPossiblePhone(trimmed)
}

/**
 * DB write gate: E.164 when `isPossible`, otherwise null.
 * Call sites store only this result (or omit) so invalid numbers never hit the DB.
 */
export function toE164Phone(phone: string): string | null {
  const parsed = tryParsePossiblePhone(phone)
  return parsed ? parsed.format('E.164') : null
}
